import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import axiosInstance from '../api';
import './PolicyEditorModal.css';

const QUILL_MODULES = {
  toolbar: [
    [{ header: '1' }, { header: '2' }, { font: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['bold', 'italic', 'underline'],
    [{ align: [] }],
    ['link'],
    ['clean'],
  ],
};

/** T-0389 Phase 2: in-app HTML editor for an org's own adopted policy copy (organization_policy.
 * content_html). Save Draft only -- Publish stays the existing flow (PolicyPublishService via the
 * card's own Publish button), this modal never touches policyStatus. */
function PolicyEditorModal({ policy, onClose, onSaved }) {
  const { t } = useTranslation();
  // Seed from the (possibly stale) list-state prop so the editor renders immediately; refetched below.
  const [content, setContent] = useState(policy.contentHtml || '');
  const [refetching, setRefetching] = useState(true);
  const [refetchFailed, setRefetchFailed] = useState(false);
  // T-0412: the row_version this editor's content corresponds to. Seeded from the (possibly stale)
  // list prop, then REFRESHED by the on-open refetch below so fresh content never pairs with a stale
  // version (which would 409 a perfectly clean save). Sent on Save Draft so the backend can reject
  // (409) a save that would silently clobber a newer write.
  const [rowVersion, setRowVersion] = useState(policy.rowVersion ?? null);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState(null); // null | 'generic' | 'conflict'

  // T-0406: `policy` comes from PoliciesPage's in-memory list, which may be stale if this org's policy
  // content changed server-side since that list last loaded (e.g. another manager saved a draft, or an
  // admin re-adopted the template). Refetch the current content on open to shrink -- not eliminate -- the
  // window where this editor silently starts from and could overwrite someone else's more recent edit.
  useEffect(() => {
    let cancelled = false;
    axiosInstance.get(`/organization-policies/${policy.id}`)
      .then(res => {
        if (cancelled) return;
        setContent(res.data.contentHtml || '');
        setRowVersion(res.data.rowVersion ?? null); // keep version in lockstep with the refetched content
      })
      .catch(e => {
        console.error('Failed to refetch latest policy content, editing from possibly-stale data:', e);
        if (!cancelled) setRefetchFailed(true);
      })
      .finally(() => {
        if (!cancelled) setRefetching(false);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [policy.id]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await axiosInstance.patch(`/organization-policies/${policy.id}/content`, {
        contentHtml: content,
        rowVersion,
      });
      onSaved(res.data);
      onClose();
    } catch (err) {
      setError(err?.response?.status === 409 ? 'conflict' : 'generic');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="pem-overlay" onClick={() => !saving && onClose()}>
      <div className="pem-modal" onClick={e => e.stopPropagation()}>
        <div className="pem-header">
          <div className="pem-header-left">
            <i className="fas fa-file-signature pem-header-icon" />
            <div>
              <h2 className="pem-title">{policy.policyName}</h2>
              <p className="pem-subtitle">{t('PoliciesPage.editorSubtitle')}</p>
            </div>
          </div>
          <button className="pem-close" onClick={onClose} disabled={saving}>
            <i className="fas fa-times" />
          </button>
        </div>

        <div className="pem-body">
          {refetching ? (
            <div className="pem-loading"><i className="fas fa-spinner fa-spin" /> {t('PoliciesPage.editorLoading')}</div>
          ) : (
            <ReactQuill
              theme="snow"
              value={content}
              onChange={setContent}
              modules={QUILL_MODULES}
            />
          )}
          {refetchFailed && <p className="pem-warning">{t('PoliciesPage.editorRefetchFailed')}</p>}
          {error === 'conflict' && <p className="pem-error">{t('PoliciesPage.alertSaveDraftConflict')}</p>}
          {error === 'generic' && <p className="pem-error">{t('PoliciesPage.alertSaveDraftFailed')}</p>}
        </div>

        <div className="pem-footer">
          <button className="pem-cancel" onClick={onClose} disabled={saving}>
            {t('PoliciesPage.cancel')}
          </button>
          <button className="pem-save-btn" onClick={handleSave} disabled={saving || refetching}>
            {saving
              ? <><i className="fas fa-spinner fa-spin" /> {t('PoliciesPage.savingDraft')}</>
              : <><i className="fas fa-save" /> {t('PoliciesPage.saveDraft')}</>}
          </button>
        </div>
      </div>
    </div>
  );
}

export default PolicyEditorModal;
